/**
 * Workflow adapter layer.
 *
 * The ONLY place that knows the SendMoney workflow shape. Translates workflow
 * fields DOWN into workflow-agnostic `FieldDescriptor`s so the primitive layer
 * (fieldSchema / DynamicField) never sees `serviceProviderFieldId`,
 * `datatype`, `optionsSource`, or `relatedFields`.
 *
 * Unknown datatypes FAIL LOUDLY — never silently dropped (money form).
 */
import {
  FieldDescriptor,
  FieldKind,
  FieldOption,
  SectionDescriptor,
  WORKFLOW_DATATYPES,
  WorkflowDatatype,
  WorkflowField,
  WorkflowFieldOption,
  WorkflowFieldValue,
  WorkflowPage,
  WorkflowSection,
} from "../../types/components/dynamicForms";
import { SEND_MONEY_WORKFLOW_STEPS } from "../../constants/enums";

const KNOWN = new Set<string>(WORKFLOW_DATATYPES);

/** Thrown when a workflow field carries a datatype the engine can't map. */
export class UnknownDatatypeError extends Error {
  readonly datatype: string;
  readonly fieldId: string;
  constructor(datatype: string, fieldId: string) {
    super(
      `Unknown workflow datatype "${datatype}" on field "${fieldId}". ` +
      `Supported: ${WORKFLOW_DATATYPES.join(", ")}.`
    );
    this.name = "UnknownDatatypeError";
    this.datatype = datatype;
    this.fieldId = fieldId;
  }
}

export function isKnownDatatype(d: string): d is WorkflowDatatype {
  return KNOWN.has(d);
}

/** datatype → primitive kind. Country, Dropdown and CascadingDropdown all collapse to "select". */
function datatypeToKind(datatype: WorkflowDatatype): FieldKind {
  switch (datatype) {
    case "Text":
      return "text";
    case "Email":
      return "email";
    case "PhoneNumber":
    case "PhoneNumberFixedCountry":
      return "phone";
    case "Country":
    case "Dropdown":
    case "CascadingDropdown":
      return "select";
    default: {
      const _exhaustive: never = datatype;
      throw new Error(`Unhandled datatype: ${String(_exhaustive)}`);
    }
  }
}

/**
 * Workflow options → FieldOptions. Drops the "--Select--" placeholder, which the
 * server sends with no value (workflow payload) or `value: null` (optionsSource
 * response) — the select renders its own placeholder.
 */
export function fromWorkflowOptions(options: WorkflowFieldOption[]): FieldOption[] {
  return options.reduce<FieldOption[]>((acc, option) => {
    if (typeof option.value === "string") {
      acc.push({ label: option.label, value: option.value, disabled: option.disabled });
    }
    return acc;
  }, []);
}

/** Map one workflow field to a FieldDescriptor, or throw on unknown datatype. */
export function fromWorkflowField(wf: WorkflowField): FieldDescriptor {
  if (!isKnownDatatype(wf.datatype)) {
    throw new UnknownDatatypeError(wf.datatype, wf.serviceProviderFieldId);
  }
  return {
    name: wf.serviceProviderFieldId, // adapter supplies the RHF key
    kind: datatypeToKind(wf.datatype),
    label: wf.name,
    placeholder: wf.placeholderText,
    helpHtml: wf.subLabelText,
    constraints: {
      required: wf.isRequired,
      min: wf.min,
      max: wf.max,
      pattern: wf.regex,
    },
    options: wf.options ? fromWorkflowOptions(wf.options) : undefined,
    // optionsSource tokens are serviceProviderFieldIds — the same keys the adapter uses for `name`
    optionsUrl: wf.datatype === "CascadingDropdown" ? wf.optionsSource : undefined,
    readOnly: wf.isReadOnly,
    fixedCountry: wf.datatype === "PhoneNumberFixedCountry" || undefined,
    value: wf.value,
  };
}

/** Map a set of workflow fields. Fails loudly on the first unknown datatype. */
export function fromWorkflowFields(fields: WorkflowField[]): FieldDescriptor[] {
  return fields.map(fromWorkflowField);
}

/** Map a page's sections to renderer sections. */
export function fromWorkflowSections(sections: WorkflowSection[]): SectionDescriptor[] {
  return sections.map((section) => ({
    infoHtml: section.info || undefined,
    fields: fromWorkflowFields(section.fields),
  }));
}

/* -------------------------------------------------------------------------- */
/* Page helpers                                                               */
/* -------------------------------------------------------------------------- */

/** Pages ordered by sequenceId (the ordering key; `id` is not stable). */
export function sortPages(pages: WorkflowPage[]): WorkflowPage[] {
  return [...pages].sort((a, b) => a.sequenceId - b.sequenceId);
}

/** "Recipient Details" → "recipient-details". Empty result falls back to the sequenceId. */
function slugify(title: string, sequenceId: number): string {
  const slug = title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || `step-${sequenceId}`;
}

/**
 * URL step slug per page, in page order, from each page's title. Duplicate
 * titles get a numeric suffix so every step stays addressable; `review` is
 * reserved for the Review step.
 */
export function stepSlugs(pages: WorkflowPage[]): string[] {
  const seen = new Map<string, number>([[SEND_MONEY_WORKFLOW_STEPS.REVIEW, 1]]);
  return pages.map((page) => {
    const base = slugify(page.title, page.sequenceId);
    const count = (seen.get(base) ?? 0) + 1;
    seen.set(base, count);
    return count === 1 ? base : `${base}-${count}`;
  });
}

function fieldsOfPage(page: WorkflowPage): WorkflowField[] {
  return page.sections.reduce<WorkflowField[]>((acc, s) => acc.concat(s.fields), []);
}

function fieldsOfPages(pages: WorkflowPage[]): WorkflowField[] {
  return pages.reduce<WorkflowField[]>((acc, p) => acc.concat(fieldsOfPage(p)), []);
}

/** Server-supplied values (prefill / 226 echo), keyed by serviceProviderFieldId. */
export function valuesFromPages(pages: WorkflowPage[]): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  for (const field of fieldsOfPages(pages)) {
    if (field.value !== undefined) values[field.serviceProviderFieldId] = field.value;
  }
  return values;
}

/** First server validation message per field on a page. */
export function validationErrorsOf(page: WorkflowPage): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of fieldsOfPage(page)) {
    const message = field.validationError?.[0]?.errorMessage;
    if (message) errors[field.serviceProviderFieldId] = message;
  }
  return errors;
}

/** Index of the first page carrying a server validation error, or -1. */
export function firstInvalidPageIndex(pages: WorkflowPage[]): number {
  return pages.findIndex((p) => Object.keys(validationErrorsOf(p)).length > 0);
}

/** Every field across every page, with its current value, for submission. */
export function toSubmissionFields(
  pages: WorkflowPage[],
  values: Record<string, unknown>
): WorkflowFieldValue[] {
  return fieldsOfPages(pages).map((field) => ({
    serviceProviderFieldId: field.serviceProviderFieldId,
    value: values[field.serviceProviderFieldId] ?? "",
  }));
}

/* -------------------------------------------------------------------------- */
/* Other data sources → FieldDescriptor (proof the primitive layer is reusable) */
/* -------------------------------------------------------------------------- */

/** A country as returned by the GetCountries endpoint. */
export interface CountryOption {
  name: string;
  iso: string;
}

/**
 * Build a standalone country `select` descriptor from the countries endpoint —
 * NO workflow involved. Same primitive kind, same buildZodSchema path as a
 * workflow Dropdown.
 */
export function countryFieldFromCountries(
  countries: CountryOption[],
  opts: { name?: string; label?: string; required?: boolean } = {}
): FieldDescriptor {
  const options: FieldOption[] = countries.map((c) => ({
    label: c.name,
    value: c.iso,
  }));
  return {
    name: opts.name ?? "country",
    kind: "select",
    label: opts.label ?? "Country",
    constraints: { required: opts.required ?? true },
    options,
  };
}